/* eslint-disable jsx-a11y/anchor-is-valid */
import { groupBy } from 'underscore';
import React, { useContext, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Link, Divider, makeStyles, tokens, mergeClasses } from '@fluentui/react-components';
import { withCustomValidation } from '@bingads-webui-react/validation';

import { IMAAudienceEstimation } from './audience-estimation/ima-audience-estimation';
import { InterestsSelector } from './interests-selector/interests-selector';
import { formatIMATree } from './utils';

const groupInterests = (interests) => {
  const groupedInterests = groupBy(interests, 'Status');
  return {
    targetedInterests: groupedInterests.Active || [],
    excludedInterests: groupedInterests.Excluded || [],
  };
};

export const formatInterests = ({ interests, i18n }) => {
  if (!interests || !interests.length) {
    return i18n.getString(_TL_('Targeting: No interests selected.'));
  }

  const { targetedInterests, excludedInterests } = groupInterests(interests);
  let targetedInterestDetails = '';
  let excludedInterestDetails = '';

  if (targetedInterests.length === 1) {
    targetedInterestDetails = i18n.getString(_TL_('Targeted: 1 interest'));
  } else if (targetedInterests.length > 1) {
    targetedInterestDetails = i18n.getString(_TL_('Targeted: {{count}} interests'), { count: (targetedInterests.length) });
  }

  if (excludedInterests.length === 1) {
    excludedInterestDetails = i18n.getString(_TL_('Excluded: 1 interest'));
  } else if (excludedInterests.length > 1) {
    excludedInterestDetails = i18n.getString(_TL_('Excluded: {{count}} interests'), { count: (excludedInterests.length) });
  }

  return `${targetedInterestDetails}${targetedInterestDetails && excludedInterestDetails && i18n.getString(_TL_('. '))}${excludedInterestDetails}`;
};

const useStyles = makeStyles({
  container: {
    marginBottom: tokens.spacingVerticalM,
  },
  interestsHeader: {
    marginBottom: tokens.spacingVerticalM,
  },
  divider: {
    marginTop: tokens.spacingVerticalL,
    marginBottom: tokens.spacingVerticalL,
  },
});

const AudienceInterests = React.memo(({
  supportExclusion,
  isAIMCampaignEnabled,
  locationTargets,
  audienceList,
  campaignType,
  validationProps,
  updateValidation,
  autoTargeting,
  campaignBiddingStrategy,
  campaignSubType,
  bundleLocationCriterions,
}) => {
  const classes = useStyles();

  const {
    data: { interests },
  } = validationProps;
  const i18n = useContext(I18nContext);
  const { IMATreeItems: treeItems, IMATreeItemIdxMap: treeItemIdxMap, IdMap: audienceIdMap } = useMemo(() => {
    if (!audienceList) {
      return {};
    }
    return formatIMATree(audienceList);
  }, [audienceList]);
  const { targetedInterests, excludedInterests } = useMemo(() => groupInterests(interests), [interests]);
  const { targetedItems, excludedItems } = useMemo(() => {
    if (!audienceIdMap) {
      return {
        targetedItems: [],
        excludedItems: [],
      };
    }
    const targeted = targetedInterests.map(({ Criterion }) => audienceIdMap[Criterion.AudienceId].value);
    const excluded = excludedInterests.map(({ Criterion }) => audienceIdMap[Criterion.AudienceId].value);
    return { targetedItems: targeted, excludedItems: excluded };
  }, [targetedInterests, excludedInterests, audienceIdMap]);

  const [expandExclusions, setExpandExclusions] = useState(excludedItems.length > 0);

  const onTarget = useCallback((newTargeted) => {
    const newInterests = newTargeted.map((interest) => {
      const interestData = treeItems[treeItemIdxMap[interest]].data;
      return {
        Status: 'Active',
        Criterion: {
          AudienceId: interestData.Id,
          AudienceName: interestData.Name,
          AudienceType: 'InMarket',
          BidMultiplier: 0,
          Type: 'Audience',
        },
        BidType: 'TargetAndBid',
      };
    });
    const remainingSelected = targetedInterests.filter(interest => newInterests.some(newInterest => newInterest.Criterion.AudienceId === interest.Criterion.AudienceId));
    const newlySelected = newInterests.filter(newInterest => !targetedInterests.some(interest => interest.Criterion.AudienceId === newInterest.Criterion.AudienceId));
    updateValidation([], { interests: [...excludedInterests, ...remainingSelected, ...newlySelected] });
  }, [treeItemIdxMap, updateValidation, targetedInterests, excludedInterests, treeItems]);

  const onExclude = useCallback((newExcluded) => {
    const newInterests = newExcluded.map((interest) => {
      const interestData = treeItems[treeItemIdxMap[interest]].data;
      return {
        Status: 'Excluded',
        Criterion: {
          AudienceId: interestData.Id,
          AudienceName: interestData.Name,
          AudienceType: 'InMarket',
          BidMultiplier: 0,
          Type: 'Audience',
        },
        BidType: 'Exclude',
      };
    });
    const remainingSelected = excludedInterests.filter(interest => newInterests.some(newInterest => newInterest.Criterion.AudienceId === interest.Criterion.AudienceId));
    const newlySelected = newInterests.filter(newInterest => !excludedInterests.some(interest => interest.Criterion.AudienceId === newInterest.Criterion.AudienceId));
    updateValidation([], { interests: [...targetedInterests, ...remainingSelected, ...newlySelected] });
  }, [treeItemIdxMap, updateValidation, excludedInterests, targetedInterests, treeItems]);

  const onExpandExclusions = useCallback(() => {
    setExpandExclusions(true);
  }, []);

  if (!treeItems || !treeItemIdxMap) {
    return null;
  }

  return (
    <div className={mergeClasses('audience-signal-interests', classes.container)}>
      <IMAAudienceEstimation
        targetedInterests={targetedInterests}
        excludedInterests={excludedInterests}
        i18n={i18n}
        autoTargeting={autoTargeting}
        campaignBiddingStrategy={campaignBiddingStrategy}
        campaignSubType={campaignSubType}
      />
      <div className={classes.interestsHeader}>
        <div>{i18n.getString(_TL_('Reach people who are ready to buy or interested in:'))}</div>
      </div>
      <InterestsSelector
        containerClassName="targeted-interests"
        locationTargets={locationTargets}
        treeItems={treeItems}
        treeItemIdxMap={treeItemIdxMap}
        isAIMCampaignEnabled={isAIMCampaignEnabled}
        campaignType={campaignType}
        selectedItems={targetedItems}
        disabledItems={excludedItems}
        onChange={onTarget}
        bundleLocationCriterions={bundleLocationCriterions}
      />
      {supportExclusion && !expandExclusions && (
      <Link onClick={onExpandExclusions}>{i18n.getString(_TL_('Add exclusions'))}</Link>
        )}
      {supportExclusion && expandExclusions && (
        <>
          <Divider className={classes.divider} />
          <div className={classes.interestsHeader}>
            {i18n.getString(_TL_('Exclude people who are looking to buy:'))}
          </div>
          <InterestsSelector
            containerClassName="excluded-interests"
            locationTargets={locationTargets}
            treeItems={treeItems}
            treeItemIdxMap={treeItemIdxMap}
            isAIMCampaignEnabled={isAIMCampaignEnabled}
            campaignType={campaignType}
            selectedItems={excludedItems}
            disabledItems={targetedItems}
            onChange={onExclude}
            bundleLocationCriterions={bundleLocationCriterions}
          />
        </>
        )}
    </div>
  );
});

AudienceInterests.propTypes = {
  supportExclusion: PropTypes.bool,
  isAIMCampaignEnabled: PropTypes.bool.isRequired,
  campaignType: PropTypes.string.isRequired,
  locationTargets: PropTypes.shape({
    useParentLevel: PropTypes.bool.isRequired,
    parentLevelSettings: PropTypes.shape({}),
    currentLevelSettings: PropTypes.shape({}),
    peopleInLocation: PropTypes.number,
  }).isRequired,
  audienceList: PropTypes.arrayOf(PropTypes.shape({})),
  classes: PropTypes.shape({
    interestsHeader: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
  }).isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      interests: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    audienceListInFlatTree: PropTypes.arrayOf(PropTypes.shape({})),
    valueToFlatItem: PropTypes.shape({}),
    valueToAudience: PropTypes.shape({}),
    interests: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  autoTargeting: PropTypes.string.isRequired,
  campaignBiddingStrategy: PropTypes.string.isRequired,
  campaignSubType: PropTypes.string.isRequired,
  bundleLocationCriterions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

AudienceInterests.defaultProps = {
  supportExclusion: true,
  audienceList: undefined,
};

export const ValidatedAudienceInterests = withCustomValidation(AudienceInterests);
